import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { captureMessage } from '@sentry/react';
import clsx from 'clsx';

import { Schema } from '@typings';
import { getNestedValue, getNonNullishMetadata } from '@utils';
import { isSchemaContainer, isSchemaField } from '@utils/guards';

import { Field, Render } from '@components/Shared';

import { SchemaAppIntegrations } from '../SchemaAppIntegrations';

import { SchemaCollapsible } from './SchemaCollapsible';
import { SchemaNode } from './SchemaNode';

const PRIMITIVE_TYPES = ['number', 'integer', 'float', 'string'];

const makeOption = ({ title, name }: Schema.Model, index: number) => {
  return (
    <option key={index} value={index}>
      {title ?? name}
    </option>
  );
};

type Props = {
  schema: Schema.AnyOf;
};

export const SchemaAnyOf: React.FC<Props> = ({
  schema,
  schema: { name, children: nodes, default: defaultValue, isCollapsibleField },
}) => {
  const { register, unregister, formState, setValue, clearErrors } =
    useFormContext();

  const extractNullishNode = (schema: Schema.Model) =>
    isSchemaField(schema) && schema.properties.type === 'null';

  const displayComponent = useMemo(() => {
    if (nodes.length === 2 && nodes.some(extractNullishNode)) {
      return 'checkbox';
    }

    if (isCollapsibleField) {
      return 'collapsible';
    }

    return 'select';
  }, [isCollapsibleField, nodes]);

  const getSelectedIndex = (value: unknown) => {
    if (value === undefined) {
      return 0;
    }

    const nullIndex = Math.max(0, nodes.findIndex(extractNullishNode));

    if (value === null || value === 'null') {
      return nullIndex;
    }

    if (displayComponent === 'select') {
      let selectedOption = nullIndex;

      // try to find the index based on the value type
      // eslint-disable-next-line default-case
      switch (typeof value) {
        case 'string': {
          selectedOption = nodes.findIndex(
            (node) => isSchemaField(node) && node.properties.type === 'string',
          );
          break;
        }

        case 'object': {
          const keys = Object.keys(value).map((key) => `${name}.${key}`);

          selectedOption = nodes.findIndex(
            (node) =>
              isSchemaContainer(node) &&
              node.children.some((child) => keys.includes(child.name)),
          );

          break;
        }
      }

      return selectedOption !== -1 ? selectedOption : nullIndex;
    }

    return nullIndex === 0 ? 1 : 0;
  };

  const [selectedIndex, setSelectedIndex] = useState(
    getSelectedIndex(
      getNestedValue(formState.defaultValues, name) ?? defaultValue,
    ),
  );

  const handleStateChange = (index: number) => {
    const newNode = nodes[index];

    const isNullishState = !!extractNullishNode(newNode);

    const isPrimitive =
      isSchemaField(newNode) &&
      PRIMITIVE_TYPES.includes(newNode.properties.type);

    const isBoolean =
      isSchemaField(newNode) && newNode.properties.type === 'boolean';

    /**
     * When state changes, transform form state to meet `anyOf` node schema.
     * Reset validation errors on state change
     */
    if (isNullishState) {
      setValue(name, null);
    } else if (isPrimitive) {
      setValue(name, '');
    } else if (isBoolean) {
      setValue(name, false);
    } else {
      setValue(name, {});
    }

    clearErrors(name);
    setSelectedIndex(index);
  };

  useEffect(() => {
    /**
     * Track `anyOf` schemas wrapper,
     * to set `null` value on initialization
     */
    register(name);

    return () => {
      try {
        unregister(name);
      } catch {
        captureMessage(`Failed to unregister ${name}`);
      }
    };
  }, [name, register, unregister]);

  useEffect(() => {
    const newDefaultValue = getNestedValue(formState.defaultValues, name);

    // when we import input config, we need to update state if value is not null
    if (newDefaultValue) {
      handleStateChange(getSelectedIndex(newDefaultValue));
      setValue(name, newDefaultValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.defaultValues]);

  const selectedNode = nodes[selectedIndex];

  if (!selectedNode) {
    return (
      <p>
        Node [{name}]: not found for index {selectedIndex}
      </p>
    );
  }

  const isNullishState = !!extractNullishNode(selectedNode);

  const handleToggle = (newState: boolean) => {
    const index = nodes.findIndex((schema) => {
      const isNullishSchema = extractNullishNode(schema);

      return newState ? !isNullishSchema : isNullishSchema;
    });

    handleStateChange(index);
  };

  switch (displayComponent) {
    case 'collapsible': {
      const { title, description } = getNonNullishMetadata(schema);

      return (
        <SchemaCollapsible
          title={title}
          description={description}
          hasError={!!getNestedValue(formState.errors, name)}
          onToggle={handleToggle}
        >
          <Render if={!isNullishState}>
            <SchemaAppIntegrations className="mt-4" schema={schema}>
              <SchemaNode withFormatter schema={selectedNode} />
            </SchemaAppIntegrations>
          </Render>
        </SchemaCollapsible>
      );
    }

    case 'checkbox': {
      const { title, description } = getNonNullishMetadata(schema);

      const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>,
      ) => {
        handleToggle(event.target.checked);
      };

      return (
        <div>
          <Field.Checkbox
            label={title}
            note={description}
            checked={!isNullishState}
            containerClassName="mb-4"
            onChange={handleCheckboxChange}
          >
            {title}
          </Field.Checkbox>

          <SchemaAppIntegrations
            className={clsx({ 'mb-4': !isNullishState })}
            schema={schema}
          >
            <div className="ml-4">
              <SchemaNode withFormatter schema={selectedNode} />
            </div>
          </SchemaAppIntegrations>
        </div>
      );
    }

    default: {
      return (
        <div>
          <Field.Select
            label={schema.title}
            value={selectedIndex}
            note={schema.description}
            containerClassName="mb-4"
            onChange={(event) => {
              handleStateChange(Number(event.target.value));
            }}
          >
            {nodes.map(makeOption)}
          </Field.Select>

          <SchemaAppIntegrations
            className={clsx({ 'mb-4': !isNullishState })}
            schema={schema}
          >
            <div className="ml-4">
              <SchemaNode schema={selectedNode} />
            </div>
          </SchemaAppIntegrations>
        </div>
      );
    }
  }
};
